import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const TermsOfService = () => {
  return (
    <>
      <SEO
        title="Terms of Service | TPS Housing"
        description="TPS Housing terms of service. Read the terms and conditions governing the use of our temporary housing placement services."
        canonicalUrl="/terms-of-service"
      />
      <div className="min-h-screen">
        <Navigation />
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-4xl font-bold text-foreground mb-2">Terms of Service</h1>
            <p className="text-muted-foreground mb-10">Last Modified: February 12, 2025</p>

            <div className="prose prose-lg max-w-none space-y-8 text-foreground/90">

              {/* Facts-style intro box */}
              <section className="bg-muted/30 rounded-xl p-6 border border-border">
                <h2 className="text-2xl font-semibold text-foreground">Overview</h2>
                <p className="text-lg font-medium text-primary">
                  These Terms of Service govern your use of TPS Housing's website and temporary housing placement services.
                </p>
              </section>

              {/* Acceptance */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Acceptance of Terms</h2>
                <p>
                  By accessing or using the TPS Housing website at <a href="https://tpshousing.com" className="text-primary hover:underline">tpshousing.com</a> and our temporary housing placement services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
              </section>

              {/* Description of Services */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Description of Services</h2>
                <p>
                  TPS Housing provides temporary housing placement services for families displaced by insurance claims. Our services include connecting insurance adjusters, policyholders, and property owners to facilitate housing placements during the claims process. Specific services include:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Temporary housing search and placement coordination</li>
                  <li>Insurance claim intake and management</li>
                  <li>Property listing and landlord coordination</li>
                  <li>Adjuster services and communication facilitation</li>
                  <li>Fair Rental Value (FRV) analysis and reporting</li>
                </ul>
              </section>

              {/* User Accounts */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">User Accounts</h2>
                <p>When creating and maintaining an account on our platform, you agree to the following:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>You must provide accurate and complete information when creating an account</li>
                  <li>You are responsible for maintaining the confidentiality of your login credentials</li>
                  <li>You are responsible for all activities that occur under your account</li>
                  <li>You must notify us immediately of any unauthorized use of your account</li>
                </ul>

                <div className="bg-muted/30 rounded-lg p-4 mt-4 border border-border">
                  <p className="text-sm">
                    <strong>Note:</strong> TPS Housing reserves the right to suspend or disable accounts that violate these terms, contain inaccurate information, or are used for unauthorized purposes.
                  </p>
                </div>
              </section>

              {/* User Responsibilities */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">User Responsibilities</h2>
                <p>When using our services, you agree to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide truthful and accurate claim and property information</li>
                  <li>Use the platform only for its intended purpose</li>
                  <li>Comply with all applicable local, state, and federal laws</li>
                  <li>Not engage in fraudulent, misleading, or deceptive practices</li>
                  <li>Not attempt to gain unauthorized access to any part of the platform</li>
                  <li>Not upload or transmit any malicious code, viruses, or harmful content</li>
                </ul>
              </section>

              {/* Insurance Claims & Placements - Table format */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Insurance Claims and Housing Placements</h2>
                <p>
                  TPS Housing acts as an intermediary to facilitate temporary housing placements. We do not guarantee the availability, condition, or suitability of any property. All housing arrangements are subject to property owner approval and availability.
                </p>

                <div className="overflow-x-auto mt-4">
                  <table className="w-full border-collapse border border-border text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="border border-border p-3 text-left font-semibold">Responsibility</th>
                        <th className="border border-border p-3 text-left font-semibold">TPS Housing</th>
                        <th className="border border-border p-3 text-left font-semibold">User</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-border p-3">Accuracy of claim information</td>
                        <td className="border border-border p-3">Verification of submitted data</td>
                        <td className="border border-border p-3">Providing truthful, complete information</td>
                      </tr>
                      <tr className="bg-muted/30">
                        <td className="border border-border p-3">Property condition and suitability</td>
                        <td className="border border-border p-3">Reasonable due diligence</td>
                        <td className="border border-border p-3">Inspection prior to occupancy</td>
                      </tr>
                      <tr>
                        <td className="border border-border p-3">Insurance documentation</td>
                        <td className="border border-border p-3">Secure storage and processing</td>
                        <td className="border border-border p-3">Providing valid, current documents</td>
                      </tr>
                      <tr className="bg-muted/30">
                        <td className="border border-border p-3">Lease and rental agreements</td>
                        <td className="border border-border p-3">Facilitation and coordination</td>
                        <td className="border border-border p-3">Review and execution of agreements</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="mt-4">
                  Claim information submitted through our platform must be accurate and supported by valid insurance documentation. TPS Housing reserves the right to verify claim details and reject submissions that appear incomplete or fraudulent.
                </p>
              </section>

              {/* Payments and Fees */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Payments and Fees</h2>
                <p>
                  Payment terms, rental rates, and fees are established between the relevant parties (adjusters, policyholders, and landlords) during the placement process. TPS Housing may charge service fees as outlined in individual service agreements.
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>All fees will be clearly communicated prior to service delivery</li>
                  <li>Payment obligations survive termination of services</li>
                  <li>Disputes regarding fees must be raised within 30 days of billing</li>
                </ul>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Intellectual Property</h2>
                <p>
                  All content on the TPS Housing website — including text, graphics, logos, and software — is the property of TPS Housing and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our written consent.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Limitation of Liability</h2>
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <p>
                    To the fullest extent permitted by law, TPS Housing shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability shall not exceed the fees paid by you in the twelve months preceding the claim.
                  </p>
                  <p className="mt-2">
                    TPS Housing provides its services on an "as is" and "as available" basis without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
                  </p>
                </div>
              </section>

              {/* Indemnification */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Indemnification</h2>
                <p>
                  You agree to indemnify and hold harmless TPS Housing, its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the platform, your violation of these terms, or your infringement of any third-party rights.
                </p>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Termination</h2>
                <p>
                  We reserve the right to suspend or terminate your account at any time, with or without cause and with or without notice. Upon termination:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Your right to use the services will immediately cease</li>
                  <li>Any pending placements may be cancelled or transferred</li>
                  <li>Outstanding payment obligations remain enforceable</li>
                  <li>Provisions that by their nature should survive termination will remain in effect</li>
                </ul>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Governing Law</h2>
                <p>
                  These Terms of Service shall be governed by and construed in accordance with the laws of the State of Arizona, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts located in Maricopa County, Arizona.
                </p>
              </section>

              {/* Changes */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Changes to These Terms</h2>
                <p>
                  We may update these Terms of Service from time to time. We will notify you of any material changes by posting the updated terms on this page with a revised effective date. Continued use of the services after changes constitutes acceptance of the new terms.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Questions?</h2>
                <p>If you have questions about these Terms of Service, please contact us:</p>
                <ul className="list-none space-y-1 mt-2">
                  <li><strong>Email:</strong> <a href="mailto:info@tpshousing.com" className="text-primary hover:underline">info@tpshousing.com</a></li>
                  <li><strong>Phone:</strong> <a href="tel:760-687-1001" className="text-primary hover:underline">760-687-1001</a></li>
                  <li><strong>Address:</strong> 10810 N. Tatum Blvd. #102-222, Phoenix, AZ 85028</li>
                </ul>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default TermsOfService;